import { useFormik } from "formik";
import SidebarComponent from "../components/common/SidebarComponent";
import * as yup from "yup";
import { InputAdornment, TextField } from "@mui/material";
import { useContext, useState } from "react";
import { RxEyeClosed } from "react-icons/rx";
import { VscEye } from "react-icons/vsc";
import { GlobalStateContext } from "../components/context/GlobalStateContext";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getDetailUserProfile,
  removeImageProfile,
  updateUserProfile,
  uploadImageProfile,
} from "../services/profileApi";
import { FaUserCircle } from "react-icons/fa";
import { useNotification } from "../components/context/NotificationContext";
import { IoInformationCircleOutline, IoTrashOutline } from "react-icons/io5";
import { updateDoctorPassword } from "../services/authApi";

const textFieldStyles = {
  backgroundColor: "white",
  borderRadius: "10px",
  color: "#111827",
  transition: "all 0.4s",
  fontFamily: '"Montserrat", sans-serif',
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: "#d1d5db",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#9ca3af",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "1px",
  },
  "& .MuiInputBase-input": {
    padding: "10px 12px",
    color: "#111827",
    fontSize: { xs: "14px", lg: "16px" },
    fontFamily: '"Montserrat", sans-serif',
    "&::placeholder": {
      color: "#CBCBCB",
      opacity: 1,
      fontFamily: '"Montserrat", sans-serif',
    },
  },
  "&.Mui-disabled": {
    backgroundColor: "#f9fafb",
    opacity: 0.7,
  },
};

const errorTextFieldStyles = {
  ...textFieldStyles,
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: "#ef4444",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ef4444",
  },
};

const formHelperFieldStyles = {
  width: "100%",
  display: "flex",
  justifyContent: "start",
  margin: "2px 0 0 0",
  fontSize: "12px",
  color: "#ef4444",
  fontFamily: '"Montserrat", sans-serif',
};

const validationSchema = yup.object({
  old_password: yup
    .string()
    .min(8, "Password minimal 8 karakter")
    .required("Password wajib diisi"),

  new_password: yup
    .string()
    .min(8, "Password minimal 8 karakter")
    .notOneOf(
      [yup.ref("old_password")],
      "Password baru tidak boleh sama dengan password lama",
    )
    .required("Password wajib diisi"),

  confirmPassword: yup
    .string()
    .oneOf([yup.ref("new_password"), null], "Password tidak sama")
    .required("Konfirmasi password wajib diisi"),
});

const DoctorProfilePage = () => {
  const { isLogin } = useContext(GlobalStateContext);
  const { showNotification } = useNotification();

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const getFieldStyles = (hasError) => {
    return hasError ? errorTextFieldStyles : textFieldStyles;
  };

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["detail-user-profile"],
    queryFn: () => getDetailUserProfile(),
    enabled: isLogin === true,
    keepPreviousData: false,
  });

  const { mutate: mutateUpdate } = useMutation({
    mutationFn: updateUserProfile,
    onSuccess: () => {
      refetch();
      showNotification("Berhasil memperbaharui foto profil", "success");
    },
    onError: (err) => {
      setErrorMessage(err.message.id || "Terjadi kesalahan saat mendaftar");
    },
  });

  const { mutate: mutateUploadImage } = useMutation({
    mutationFn: uploadImageProfile,
    onSuccess: (data) => {
      mutateUpdate({
        profile_image: data.image_url,
        action_type: 2,
      });
    },
    onError: (err) =>
      showNotification(err.message.id ? err.message.id : err.message, "error"),
  });

  const { mutate: mutateDeleteImage } = useMutation({
    mutationFn: removeImageProfile,
    onSuccess: () => {
      mutateUpdate({
        profile_image: "",
        action_type: 2,
      });
    },
    onError: (err) =>
      showNotification(err.message.id ? err.message.id : err.message, "error"),
  });

  const { mutate: mutateUpdatePassword, isPending } = useMutation({
    mutationFn: updateDoctorPassword,
    onSuccess: () => {
      formik.resetForm();
      refetch();
      showNotification("Berhasil memperbaharui password", "success");
    },
    onError: (err) => {
      formik.resetForm();
      showNotification(err.message.id ? err.message.id : err.message, "error");
    },
  });

  const formik = useFormik({
    initialValues: {
      old_password: "",
      new_password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: (value) => {
      mutateUpdatePassword(value);
    },
    validateOnMount: true,
  });

  return (
    <div className="flex bg-indigo-50">
      <div className="w-0 lg:w-[96px] h-[100vh] sticky top-0 z-30">
        <SidebarComponent />
      </div>

      <div className="w-[100%] lg:w-[calc(100%-96px)]">
        <div className="overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold text-slate-700 mb-6">Profil</h1>

            <div className="bg-white rounded-2xl p-6 mb-10">
              <div className="border rounded-xl bg-slate-50 p-6 flex items-center gap-6 mb-6">
                <div className="relative">
                  {data && data.profile_image === "" && !isFetching ? (
                    <div className="relative">
                      <FaUserCircle className="w-[150px] h-[150px] text-gray-300" />
                    </div>
                  ) : isFetching ? (
                    <div className="w-[150px] h-[150px] rounded-full bg-slate-200 border-2 border-gray-200"></div>
                  ) : (
                    <>
                      <img
                        src={data && data.profile_image}
                        alt="Profile IMG"
                        className="w-[150px] h-[150px] rounded-full object-cover border-2 border-gray-200"
                      />
                      <div
                        onClick={() =>
                          mutateDeleteImage(data && data.profile_image)
                        }
                        className="p-2 bg-white border shadow-md border-gray-300 rounded-full absolute bottom-0 right-0 cursor-pointer hover:bg-red-50 transition-colors duration-200"
                      >
                        <IoTrashOutline size={20} className="text-error" />
                      </div>
                    </>
                  )}
                </div>

                <div className="text-slate-700">
                  {data && !isFetching ? (
                    <h1 className="text-2xl font-semibold mb-1">
                      {data.fullname}
                    </h1>
                  ) : (
                    <div className="h-8 rounded-md w-3/4 bg-slate-200 mb-1"></div>
                  )}
                  <div className="font-medium mb-4 flex items-center gap-2">
                    <p>SIP:</p>{" "}
                    {data && !isFetching ? (
                      <span>{data.no_sip}</span>
                    ) : (
                      <div className="h-4 rounded-md w-1/4 bg-slate-200"></div>
                    )}
                  </div>
                  <label
                    htmlFor="dropzone-file2"
                    disabled={isFetching}
                    className="cursor-pointer mb-2 px-4 h-[36px] border text-md text-white flex items-center gap-2 justify-center w-fit tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline transform duration-200 hover:scale-100 hover:bg-secondaryHover border-secondary bg-secondary disabled:border-slate-300 disabled:bg-slate-300 disabled:cursor-default"
                  >
                    <input
                      id="dropzone-file2"
                      type="file"
                      accept="image/jpeg, image/jpg, image/png"
                      onChange={(e) => mutateUploadImage(e.target.files[0])}
                      hidden
                    />
                    <p>Ganti Foto</p>
                  </label>

                  <p className="text-md text-slate-500">
                    Disarankan foto menggunakan jas dokter/snelli.
                  </p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="w-full border rounded-xl bg-white p-6 space-y-6">
                  <h1 className="text-2xl font-semibold text-slate-700">
                    Informasi Personal
                  </h1>
                  <div>
                    <p className="text-slate-500 text-base">
                      Nama Lengkap & Gelar
                    </p>
                    {data && !isFetching ? (
                      <p className="text-slate-700 font-medium">
                        {data.fullname}
                      </p>
                    ) : (
                      <div className="h-6 rounded-md w-3/4 bg-slate-200 mb-1"></div>
                    )}
                  </div>
                  <div>
                    <p className="text-slate-500 text-base">No SIP</p>
                    {data && !isFetching ? (
                      <p className="text-slate-700 font-medium">
                        {data.no_sip}
                      </p>
                    ) : (
                      <div className="h-6 rounded-md w-1/4 bg-slate-200 mb-1"></div>
                    )}
                  </div>
                  <div>
                    <p className="text-slate-500 text-base">Spesialisasi</p>
                    {data && !isFetching ? (
                      <p className="text-slate-700 font-medium">
                        {data.speciality}
                      </p>
                    ) : (
                      <div className="h-6 rounded-md w-2/4 bg-slate-200 mb-1"></div>
                    )}
                  </div>
                </div>
                <div className="w-full border rounded-xl bg-white p-6 space-y-6">
                  <h1 className="text-2xl font-semibold text-slate-700">
                    Ubah Password
                  </h1>

                  <form onSubmit={formik.handleSubmit}>
                    <div className="space-y-4">
                      <div>
                        <p className="text-slate-500 text-base">
                          Password Lama
                        </p>
                        <TextField
                          fullWidth
                          type={showPassword ? "text" : "password"}
                          name="old_password"
                          placeholder="Masukkan password lama"
                          value={formik.values.old_password}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.old_password &&
                            Boolean(formik.errors.old_password)
                          }
                          helperText={
                            formik.touched.old_password &&
                            formik.errors.old_password
                              ? formik.errors.old_password
                              : ""
                          }
                          disabled={isPending}
                          InputProps={{
                            sx: getFieldStyles(
                              formik.errors.old_password &&
                                formik.touched.old_password,
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                {showPassword ? (
                                  <RxEyeClosed
                                    onClick={() =>
                                      setShowPassword(!showPassword)
                                    }
                                    className="cursor-pointer"
                                  />
                                ) : (
                                  <VscEye
                                    onClick={() =>
                                      setShowPassword(!showPassword)
                                    }
                                    className="cursor-pointer"
                                  />
                                )}
                              </InputAdornment>
                            ),
                          }}
                          FormHelperTextProps={{
                            sx: formHelperFieldStyles,
                          }}
                        />
                      </div>
                      <div>
                        <p className="text-slate-500 text-base">
                          Password Baru
                        </p>
                        <TextField
                          fullWidth
                          type={showNewPassword ? "text" : "password"}
                          name="new_password"
                          placeholder="Masukkan password (min. 8 karakter)"
                          value={formik.values.new_password}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.new_password &&
                            Boolean(formik.errors.new_password)
                          }
                          helperText={
                            formik.touched.new_password &&
                            formik.errors.new_password
                              ? formik.errors.new_password
                              : ""
                          }
                          disabled={isPending}
                          InputProps={{
                            sx: getFieldStyles(
                              formik.errors.new_password &&
                                formik.touched.new_password,
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                {showNewPassword ? (
                                  <RxEyeClosed
                                    onClick={() =>
                                      setShowNewPassword(!showNewPassword)
                                    }
                                    className="cursor-pointer"
                                  />
                                ) : (
                                  <VscEye
                                    onClick={() =>
                                      setShowNewPassword(!showNewPassword)
                                    }
                                    className="cursor-pointer"
                                  />
                                )}
                              </InputAdornment>
                            ),
                          }}
                          FormHelperTextProps={{
                            sx: formHelperFieldStyles,
                          }}
                        />
                      </div>

                      <div>
                        <p className="text-slate-500 text-base">
                          Konfirmasi Password Baru
                        </p>
                        <TextField
                          fullWidth
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          placeholder="Masukkan ulang password"
                          value={formik.values.confirmPassword}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.confirmPassword &&
                            Boolean(formik.errors.confirmPassword)
                          }
                          helperText={
                            formik.touched.confirmPassword &&
                            formik.errors.confirmPassword
                              ? formik.errors.confirmPassword
                              : ""
                          }
                          disabled={isPending}
                          InputProps={{
                            sx: getFieldStyles(
                              formik.errors.confirmPassword &&
                                formik.touched.confirmPassword,
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                {showConfirmPassword ? (
                                  <RxEyeClosed
                                    onClick={() =>
                                      setShowConfirmPassword(
                                        !showConfirmPassword,
                                      )
                                    }
                                    className="cursor-pointer"
                                  />
                                ) : (
                                  <VscEye
                                    onClick={() =>
                                      setShowConfirmPassword(
                                        !showConfirmPassword,
                                      )
                                    }
                                    className="cursor-pointer"
                                  />
                                )}
                              </InputAdornment>
                            ),
                          }}
                          FormHelperTextProps={{
                            sx: formHelperFieldStyles,
                          }}
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={!formik.isValid || formik.isSubmitting}
                        className="h-[40px] border text-white flex items-center gap-2 justify-center w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline transform duration-200 hover:scale-100 hover:bg-secondaryHover border-secondary bg-secondary disabled:border-slate-300 disabled:bg-slate-300 disabled:cursor-default"
                      >
                        {isPending ? "Memproses..." : "Simpan"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {errorMessage && (
                <div className="w-full flex justify-end mt-4">
                  <div className="flex items-center gap-2 bg-red-100 py-1.5 px-2.5 rounded-md">
                    <IoInformationCircleOutline
                      className="text-error shrink-0"
                      size={12}
                    />
                    <p className="text-sm text-red-700">{errorMessage}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorProfilePage;
